import { ClaimStatus } from '../CargoClaims/CargoClaims.types';

/*
 * The reference tables behind the cargo claims documentation.
 *
 * Kept as data rather than markup so the statuses page and the field reference
 * are one edit each when the interface changes, and so the labels can come from
 * the same `statusLabel` the claims grid uses. A status renamed on the screen
 * but not in the documentation is the failure this avoids.
 */

export type SetBy = 'azadea' | 'clover' | 'system';

export interface ClaimStatusRow {
  status: ClaimStatus;
  meaning: string;
  setBy: SetBy[];
  next: string;
  terminal?: boolean;
}

export const CLAIM_STATUSES: ClaimStatusRow[] = [
  {
    status: 'RECEIVED',
    meaning:
      'AZADEA has notified the claim and it has a reference. We are waiting for the details and the six mandatory documents.',
    setBy: ['azadea'],
    next: 'Documented, Cancelled',
  },
  {
    status: 'DOCUMENTED',
    meaning:
      'The details and all six mandatory documents have arrived. The claim is complete and waiting for Clover.',
    setBy: ['azadea'],
    next: 'In review, Cancelled',
  },
  {
    status: 'UNDER_REVIEW',
    meaning:
      'Clover has picked the claim up and is assessing it. AZADEA continues to see this as Documented.',
    setBy: ['clover'],
    next: 'Accepted, Rejected, Unsettled, Cancelled',
  },
  {
    status: 'ACCEPTED',
    meaning:
      'Clover has accepted the claim. The credit note follows and is what closes it.',
    setBy: ['clover'],
    next: 'Closed, Cancelled',
  },
  {
    status: 'UNSETTLED',
    meaning:
      'The claim is still being discussed rather than decided. It can go back to accepted or rejected.',
    setBy: ['clover'],
    next: 'Accepted, Rejected, Cancelled',
  },
  {
    status: 'REJECTED',
    meaning:
      'Clover has rejected the claim, with a stated reason that AZADEA can read.',
    setBy: ['clover'],
    next: 'Cancelled',
  },
  {
    status: 'CLOSED',
    meaning:
      'The credit note has been issued. This is the end of a settled claim.',
    setBy: ['clover'],
    next: 'Nothing, this is the end',
    terminal: true,
  },
  {
    status: 'CANCELLED',
    meaning:
      'Withdrawn by AZADEA, cancelled by Clover, or cancelled automatically after 90 days without documents.',
    setBy: ['azadea', 'clover', 'system'],
    next: 'Nothing, this is the end',
    terminal: true,
  },
];

export interface ClaimFieldSpec {
  field: string;
  sentWith: string;
  format: string;
  required: boolean;
  note?: string;
}

/*
 * Grouped by the call that carries the field rather than alphabetically: the
 * question this page answers is "what do I send with this request", not "does
 * this field exist somewhere".
 */
export const CLAIM_FIELDS: ClaimFieldSpec[] = [
  // Notification
  {
    field: 'company',
    sentWith: 'Notification',
    format: 'Text, up to 100 characters',
    required: false,
    note: 'The company name as you hold it. Shown to whoever reviews the claim, and nothing else reads it. Send it if you have it, but it identifies nothing on its own - the company code does that.',
  },
  {
    field: 'companyCode',
    sentWith: 'Notification',
    format: 'Text, up to 35 characters',
    required: true,
    note: 'Decides which policy holder the claim belongs to, and with the shipment number it identifies the claim. Sending the same pair twice returns the first claim rather than opening a second. A code we do not recognise is still accepted, with a warning, rather than losing the claim.',
  },
  {
    field: 'shipmentNo',
    sentWith: 'Notification',
    format: 'Text, up to 50 characters',
    required: true,
    note: 'With the company code, identifies the claim.',
  },
  { field: 'brand', sentWith: 'Notification', format: 'Text, up to 100 characters', required: false },
  { field: 'hawbNo', sentWith: 'Notification', format: 'Text, up to 50 characters', required: false },
  { field: 'currentLocation', sentWith: 'Notification', format: 'Text, up to 255 characters', required: false },
  { field: 'natureOfLoss', sentWith: 'Notification', format: 'Text, up to 255 characters', required: false },
  {
    field: 'amount',
    sentWith: 'Notification',
    format: 'A number sent as text, for example "1250.00"',
    required: false,
    note: 'Total FOB. Sent as text rather than a number so that large totals keep every digit.',
  },
  { field: 'currency', sentWith: 'Notification', format: 'Three letters, for example USD', required: false },
  { field: 'claimDate', sentWith: 'Notification', format: 'Date, YYYY-MM-DD', required: false },
  { field: 'notifyingDate', sentWith: 'Notification', format: 'Date, YYYY-MM-DD', required: false },
  { field: 'contactPerson', sentWith: 'Notification', format: 'Text, up to 255 characters', required: false },
  {
    field: 'eligible',
    sentWith: 'Notification',
    format: 'true or false',
    required: false,
    note: "AZADEA's own eligibility decision. Stored so it can be traced, never used to filter what Clover sees.",
  },

  // Details
  {
    field: 'reference',
    sentWith: 'Details',
    format: 'The reference returned by the notification',
    required: true,
  },
  {
    field: 'lines',
    sentWith: 'Details',
    format: 'A list, one entry per business unit',
    required: true,
    note: 'Each call replaces every line on the claim rather than adding to them, so every call must carry the complete set.',
  },
  { field: 'lines[].businessUnit', sentWith: 'Details', format: 'Text, up to 100 characters', required: true },
  { field: 'lines[].description', sentWith: 'Details', format: 'Text, up to 500 characters', required: false },
  { field: 'lines[].quantity', sentWith: 'Details', format: 'A whole number, zero or more', required: false },
  { field: 'lines[].fobAmount', sentWith: 'Details', format: 'A number sent as text', required: false },
  { field: 'lines[].currency', sentWith: 'Details', format: 'Three letters', required: false },

  // Documents
  {
    field: 'type',
    sentWith: 'Document upload',
    format: 'One of the seven document types',
    required: true,
    note: 'Six are mandatory: invoice, packing list, AWB or BL, customs, POD and BOQ. A file sent as "other" is kept but does not count toward the six.',
  },
  {
    field: 'file',
    sentWith: 'Document upload',
    format: 'The file itself, up to 10 MB',
    required: true,
  },

  // Decision
  {
    field: 'decision',
    sentWith: 'Decision',
    format: 'Accepted, Rejected or Unsettled',
    required: true,
  },
  {
    field: 'reason',
    sentWith: 'Decision',
    format: 'Text, 3 to 500 characters',
    required: true,
    note: 'Required. This is the text AZADEA reads when a claim is rejected.',
  },

  // Credit note
  { field: 'invoiceNumber', sentWith: 'Credit note', format: 'Text, up to 50 characters', required: true },
  { field: 'invoiceDate', sentWith: 'Credit note', format: 'Date, YYYY-MM-DD', required: true },
  { field: 'amount', sentWith: 'Credit note', format: 'A number sent as text', required: true },
  { field: 'currency', sentWith: 'Credit note', format: 'Three letters', required: true },
];
